import { lazyWithRetry } from '@/utils/lazyWithRetry'
import authRoute from './authRoute'
import authOpenRoute from './authOpenRoute'
import othersRoute from './othersRoute'
import type { ComponentType } from 'react'
import type { Route, Routes } from '@/@types/routes'

/**
 * A route whose page is a named export. `lazy` wants a default export, and the
 * `{ default: m.X }` shim loses the component's type on the way through, so
 * it is restored here once rather than at every route.
 */
const named = <M,>(load: () => Promise<M>, pick: (m: M) => unknown): Route['component'] =>
    lazyWithRetry(() => load().then((m) => ({ default: pick(m) as ComponentType<unknown> }))) as unknown as Route['component']

// Playgrounds + practice areas. Previously DEV-only; now shipped to
// production as user-facing practice surfaces (reachable via the Playgrounds
// menu item). Kept as one array for a single registration point.
const devPreviewRoutes: Routes = [
          {
              key: 'devImportReviewMobilePreview',
              path: '/dev/import-review-mobile',
              component: lazyWithRetry(
                  () => import('@/pages/dev/ImportReviewMobilePreview'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              // Isolated math-editor surface for E2E formula drives — no
              // Studio/equation-block plumbing, no document seeding. The
              // math-formula-drive pipeline runs against this. DEV-only
              // (component also self-guards on import.meta.env.DEV).
              key: 'devMathPlayground',
              path: '/math-playground',
              component: lazyWithRetry(
                  () => import('@/components/math-editor/MathPlaygroundPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              // Local tutorial runner: pick one of the 100 tutorial formulas
              // → load it in the live editor. DEV-only (component self-guards
              // on import.meta.env.DEV).
              key: 'devMathPractice',
              path: '/math-practice',
              component: lazyWithRetry(
                  () => import('@/components/math-editor/MathPracticePage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              // FT-SANDBOX-SCOPE: the REAL desktop import-review UI over a
              // real (DB-backed) playground session — exercises the actual
              // pipeline, not a mock. DEV-only (component self-guards;
              // backend dev-injects the user).
              key: 'devImportReviewPlayground',
              path: '/import-review-playground',
              component: lazyWithRetry(
                  () => import('@/pages/dev/ImportReviewPlaygroundPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              // FT-SANDBOX-SCOPE: the REAL Flow editor over a real
              // (DB-backed) `is_playground` document — load-by-id +
              // continuous sync + autosave all run for real; the backend
              // just keeps the doc out of lists/quotas. `/editor-playground`
              // mints the doc then redirects to `/editor-playground/:id`,
              // which renders the real EditorPage. DEV-only (self-guards).
              key: 'devEditorPlaygroundCreate',
              path: '/editor-playground',
              component: lazyWithRetry(
                  () => import('@/pages/dev/EditorPlaygroundPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              key: 'devEditorPlayground',
              path: '/editor-playground/:id',
              component: lazyWithRetry(
                  () => import('@/pages/dev/EditorPlaygroundPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              // Block types gallery: the real BlockEditor seeded (via the
              // editor store) with one block of each type — no auth, no
              // document fetch (BlockEditor reads the store, not the API).
              // DEV-only (component self-guards).
              key: 'devBlockGallery',
              path: '/block-gallery',
              component: lazyWithRetry(
                  () => import('@/pages/dev/BlockGalleryPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              // Hub listing every dev playground as a practice area with
              // light instructions. DEV-only (component self-guards).
              key: 'devPlaygroundsIndex',
              path: '/playgrounds',
              component: lazyWithRetry(
                  () => import('@/pages/dev/PlaygroundsIndexPage'),
              ),
              authority: [],
              // In-shell (app header + sidebar) — the hub is a browse page, so
              // it lives inside the normal app chrome (not the blank,
              // full-screen layout the immersive playgrounds use).
              meta: {
                  pageContainerType: 'contained',
                  pageBackgroundType: 'plain',
              },
          },
          {
              // Isolated sharing surface: the real ShareDrawer over a
              // sandbox document, backed by an in-memory mock API (a
              // window.fetch interceptor) — no auth, no backend, just
              // `vite`. A practice area + the capture surface for the
              // sharing tutorial. DEV-only (component self-guards).
              key: 'devSharingPlayground',
              path: '/sharing-playground',
              component: lazyWithRetry(
                  () => import('@/pages/dev/SharingPlaygroundPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              // FT-SANDBOX-SCOPE: the REAL ShareDrawer over a real
              // (DB-backed) is_playground document — the authed sharing
              // flavor (public link / collaborators / team run against the
              // real API). Complements the mock `/sharing-playground`.
              // `/sharing-playground-authed` mints the doc then redirects to
              // `/sharing-playground-authed/:id`. DEV-only (self-guards).
              key: 'devSharingPlaygroundAuthedCreate',
              path: '/sharing-playground-authed',
              component: lazyWithRetry(
                  () => import('@/pages/dev/SharingPlaygroundAuthedPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              key: 'devSharingPlaygroundAuthed',
              path: '/sharing-playground-authed/:id',
              component: lazyWithRetry(
                  () => import('@/pages/dev/SharingPlaygroundAuthedPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              // FT-SANDBOX-SCOPE: the REAL CommentsPanel over a real
              // (DB-backed) is_playground document — the authed comments
              // flavor (create / reply / resolve / delete run against the
              // real API). `/comments-playground` mints the doc then
              // redirects to `/comments-playground/:id`. DEV-only (self-guards).
              key: 'devCommentsPlaygroundCreate',
              path: '/comments-playground',
              component: lazyWithRetry(
                  () => import('@/pages/dev/CommentsPlaygroundPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              key: 'devCommentsPlayground',
              path: '/comments-playground/:id',
              component: lazyWithRetry(
                  () => import('@/pages/dev/CommentsPlaygroundPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              // FT-SANDBOX-SCOPE: the REAL team management UI
              // (TeamManageDialog) over a real (DB-backed) is_playground team
              // — rename / invite / role / remove run against the real API.
              // `/teams-playground` mints the team then redirects to
              // `/teams-playground/:id`. DEV-only (self-guards).
              key: 'devTeamsPlaygroundCreate',
              path: '/teams-playground',
              component: lazyWithRetry(
                  () => import('@/pages/dev/TeamsPlaygroundPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
          {
              key: 'devTeamsPlayground',
              path: '/teams-playground/:id',
              component: lazyWithRetry(
                  () => import('@/pages/dev/TeamsPlaygroundPage'),
              ),
              authority: [],
              meta: {
                  pageContainerType: 'gutterless',
                  footer: false,
                  layout: 'blank',
              },
          },
      ]

// Routes that belong in protectedRoutes (rendered inside the authed app shell;
// anonymous hits redirect to sign-in):
//  - the authed playgrounds call real API endpoints (POST
//    /api/documents/playground, /api/teams/playground, the import-review
//    session create), so they need a logged-in session;
//  - the playgrounds hub renders in-shell (app header + sidebar) so it's a
//    normal navigable page.
// Everything else is a pure-client / mock practice area and stays open
// (math-playground also must stay open for the e2e formula driver).
const PROTECTED_PLAYGROUND_KEYS = new Set([
    'devPlaygroundsIndex',
    'devImportReviewPlayground',
    'devEditorPlaygroundCreate', 'devEditorPlayground',
    'devSharingPlaygroundAuthedCreate', 'devSharingPlaygroundAuthed',
    'devCommentsPlaygroundCreate', 'devCommentsPlayground',
    'devTeamsPlaygroundCreate', 'devTeamsPlayground',
])
// Dev-only practice surfaces: reachable when running `vite` (QA + the
// tutorial-capture pipeline) but excluded from production builds. The
// formula library /math-practice exposed now lives in the editor's "Library"
// button (folded in as the "Examples" set), so it no longer needs a
// user-facing route. /math-playground stays public (e2e formula driver +
// usable as a standalone math scratchpad).
const DEV_ONLY_PLAYGROUND_KEYS = new Set(['devMathPractice'])
const authedPlaygroundRoutes: Routes = devPreviewRoutes.filter((r) => PROTECTED_PLAYGROUND_KEYS.has(r.key))
const publicPlaygroundRoutes: Routes = devPreviewRoutes.filter(
    (r) => !PROTECTED_PLAYGROUND_KEYS.has(r.key)
        && (import.meta.env.DEV || !DEV_ONLY_PLAYGROUND_KEYS.has(r.key)),
)

export const openRoutes: Routes = [
    // After deleting an account: signed out, with the erasure date and how to keep it (3g).
    {
        key: 'accountClosed',
        path: '/account-closed',
        component: lazyWithRetry(() => import('@/pages/AccountClosedPage')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    // Terms and Privacy (launch redesign 2a/2b): public, linked from sign-up, the sign-in footer
    // and the store. Their own bar, so the blank layout.
    {
        key: 'legal.terms',
        path: '/legal/terms',
        component: lazyWithRetry(() => import('@/pages/legal/LegalPage')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    {
        key: 'legal.privacy',
        path: '/legal/privacy',
        component: lazyWithRetry(() => import('@/pages/legal/LegalPage')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    {
        // Tester-facing changelog — OPEN so a shared link / fresh load never
        // bounces through the auth-bootstrap race (hard-nav to a protected
        // route lands on the entry path). Content is public-safe.
        key: 'whatsNew',
        path: '/whats-new',
        component: lazyWithRetry(() => import('@/pages/WhatsNewPage')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    {
        key: 'sharedDocument',
        path: '/shared/:shareLink',
        component: lazyWithRetry(() => import('@/pages/SharedDocumentPage')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    {
        key: 'latexCoveragePublic',
        path: '/latex-coverage',
        component: lazyWithRetry(() => import('@/pages/LatexCoveragePublicPage')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    {
        // Spec iter 7 — invite landing page. Public so anonymous
        // visitors can read the invite (then bounce to sign-up);
        // authed visitors see Open/Decline. Same URL shape for
        // both flows: /invites/{token}.
        key: 'inviteLanding',
        path: '/invites/:token',
        component: lazyWithRetry(() => import('@/views/InviteLanding')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    // Stytch auth callback/error/success routes — must be reachable
    // regardless of session state (a user may click a verify-email
    // link while already signed in elsewhere).
    ...authOpenRoute,
    // Public practice areas — pure-client / mock playgrounds (math, block
    // gallery, sharing mock, import-review mobile) + the playgrounds hub.
    // No auth needed; math-playground stays open for the e2e formula driver.
    // The authed playgrounds live in protectedRoutes (login required).
    ...publicPlaygroundRoutes,
]

/**
 * Help, signed in or not (the user, 12 Oct: Help on the sign-in page sent you back to sign-in).
 * AllRoutes renders these in the app's frame for a signed-in author and on a plain page otherwise;
 * the articles themselves are public (/api/kb).
 */
export const helpRoutes: Routes = [
    {
        key: 'help',
        path: '/help',
        component: lazyWithRetry(() => import('@/pages/HelpPage')),
        authority: [],
        meta: { pageContainerType: 'gutterless', pageBackgroundType: 'plain' },
    },
    {
        key: 'help.article',
        path: '/help/:slug',
        component: lazyWithRetry(() => import('@/pages/HelpPage')),
        authority: [],
        meta: { pageContainerType: 'gutterless', pageBackgroundType: 'plain' },
    },
]

export const publicRoutes: Routes = [...authRoute]

export const protectedRoutes: Routes = [
    // ───── Tables (navigation handoff §6: /tables, /tables/:id) ─────
    {
        // Tables — the landing surface. Empty shows the start page;
        // otherwise the list. SPEC.md §4a.
        key: 'tables',
        path: '/tables',
        component: lazyWithRetry(() => import('@/pages/dev/TablesListing')),
        authority: [],
        meta: { pageContainerType: 'gutterless', footer: false },
    },
    {
        // Reopening a saved table: loads it by id. The tool manages its own
        // scroll in two panes, so no gutters and no footer.
        key: 'tableTool',
        path: '/tables/:tableId',
        component: lazyWithRetry(() => import('@/pages/dev/TableToolHarness')),
        authority: [],
        meta: { pageContainerType: 'gutterless', footer: false },
    },
    { key: 'legacy.devTables', path: '/dev/tables', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectDevTables), authority: [] },
    { key: 'legacy.devTableTool', path: '/dev/table-tool', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectDevTableTool), authority: [] },
    { key: 'legacy.devTableToolDoc', path: '/dev/table-tool/:tableId', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectDevTableTool), authority: [] },

    // ───── New IA: LaTeX section ─────
    {
        // The old home. Home is `/` now (navigation handoff §3).
        key: 'latex.home',
        path: '/latex/home',
        component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectToHome),
        authority: [],
    },
    {
        // Retired old documents page — redirect to the AI-integrated My Documents.
        key: 'latex.docs',
        path: '/latex/docs',
        component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectLatexDocsToDocuments),
        authority: [],
    },
    {
        // Editor Home — the welcome/landing screen for the Editor tool: create
        // (blank · Ask Lilia · wizard) or open a recent document, then edit.
        key: 'editor.home',
        path: '/editor',
        component: lazyWithRetry(() => import('@/pages/EditorHomePage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        // My Documents — shell & documents handoff §5 (phase 1). Full-bleed
        // inside the app shell (gutterless keeps the invariant appbar but drops
        // content gutters). Root '/' switches here in phase 1c.
        key: 'documents',
        path: '/documents',
        component: lazyWithRetry(() => import('@/pages/MyDocumentsPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        // Documents · Shared with me (navigation handoff §4: a view inside the
        // tool, never its own section). Same four-slot frame as My Documents.
        key: 'sharing',
        path: '/documents/shared',
        component: lazyWithRetry(() => import('@/pages/SharingPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        // Documents · For publishers: the compliance dashboard (publisher templates phase 2, 25 Oct 2026).
        key: 'documentsPublishing',
        path: '/documents/publishing',
        component: lazyWithRetry(() => import('@/pages/PublishingPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        // Documents · Recent.
        key: 'documentsRecent',
        path: '/documents/recent',
        component: lazyWithRetry(() => import('@/pages/MyDocumentsPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        // Documents · a tag.
        key: 'documentsTag',
        path: '/documents/tag/:tag',
        component: lazyWithRetry(() => import('@/pages/MyDocumentsPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    { key: 'legacy.shared', path: '/shared', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectShared), authority: [] },
    // ───── One root per tool (navigation handoff §6) ─────
    {
        key: 'templates',
        path: '/templates',
        component: lazyWithRetry(() => import('@/views/Templates')),
        authority: [],
    },
    {
        key: 'equations',
        path: '/equations',
        component: lazyWithRetry(() => import('@/views/Formulas')),
        authority: [],
    },
    {
        key: 'equations.quick',
        path: '/equations/quick',
        component: named(() => import('@/pages/QuickFormulasPage'), (m) => m.QuickFormulasPage),
        authority: [],
    },
    {
        key: 'snippets',
        path: '/snippets',
        component: lazyWithRetry(() => import('@/views/Snippets')),
        authority: [],
    },
    {
        // References: Library · Unfiled · Imports · Collections. One page, the
        // view in the address.
        key: 'references',
        path: '/references',
        component: lazyWithRetry(() => import('@/pages/BibliographyPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        key: 'references.unfiled',
        path: '/references/unfiled',
        component: lazyWithRetry(() => import('@/pages/BibliographyPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        key: 'references.imports',
        path: '/references/imports',
        component: lazyWithRetry(() => import('@/pages/BibliographyPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        key: 'references.collection',
        path: '/references/collections/:collectionId',
        component: lazyWithRetry(() => import('@/pages/BibliographyPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    { key: 'legacy.latexTemplates', path: '/latex/templates', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectLatexTemplates), authority: [] },
    { key: 'legacy.latexFormulas', path: '/latex/formulas', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectFormulas), authority: [] },
    { key: 'legacy.latexQuickFormulas', path: '/latex/formulas/quick', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectQuickFormulas), authority: [] },
    { key: 'legacy.latexSnippets', path: '/latex/snippets', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectLatexSnippets), authority: [] },
    { key: 'legacy.bibliography', path: '/latex/bibliography', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectBibliography), authority: [] },

    // ───── New IA: ePub section ─────
    {
        key: 'epub.home',
        path: '/epub/home',
        component: lazyWithRetry(() => import('@/pages/EpubHomePage')),
        authority: [],
        meta: {
            pageContainerType: 'contained',
            pageBackgroundType: 'plain',
        },
    },
    {
        key: 'epub.books',
        path: '/epub/books',
        component: lazyWithRetry(() => import('@/views/EpubDashboard')),
        authority: [],
        meta: {
            pageContainerType: 'contained',
            pageBackgroundType: 'plain',
        },
    },

    // ───── Legacy URL redirects (April 2026 IA restructure) ─────
    {
        key: 'legacy.dashboard',
        path: '/dashboard',
        component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectToHome),
        authority: [],
    },
    {
        key: 'legacy.epubDashboard',
        path: '/epub-dashboard',
        component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectEpubDashboardToEpubHome),
        authority: [],
    },
    { key: 'legacy.quickFormulas', path: '/formulas/quick', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectQuickFormulas), authority: [] },
    { key: 'legacy.formulas', path: '/formulas', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectFormulas), authority: [] },

    // ───── Standalone routes still in sidebar ─────
    {
        key: 'importExport',
        path: '/import',
        component: lazyWithRetry(() => import('@/views/ImportExport')),
        authority: [],
    },
    { key: 'legacy.importExport', path: '/import-export', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectImportExport), authority: [] },
    {
        // An app on the connector asked to import a file: the author drops it here (MCP start_import).
        key: 'connect.import',
        path: '/connect/import/:token',
        component: lazyWithRetry(() => import('@/pages/ConnectImportPage')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    {
        // An outside app (Claude, ChatGPT, any MCP client) asks to use the account: Stytch's
        // Connected Apps authorize URL. Its own full-screen card, like sign-in.
        key: 'oauth.authorize',
        path: '/oauth/authorize',
        component: lazyWithRetry(() => import('@/pages/OAuthAuthorizePage')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    {
        // 4c: back from Stripe Checkout (4 Nov 2026). The API sends Stripe these two addresses.
        key: 'billing.success',
        path: '/billing/success',
        component: lazyWithRetry(() => import('@/pages/BillingResultPage')),
        authority: [],
        meta: { pageContainerType: 'gutterless', pageBackgroundType: 'plain' },
    },
    {
        key: 'billing.cancel',
        path: '/billing/cancel',
        component: lazyWithRetry(() => import('@/pages/BillingResultPage')),
        authority: [],
        meta: { pageContainerType: 'gutterless', pageBackgroundType: 'plain' },
    },
    {
        key: 'settings',
        path: '/settings',
        component: lazyWithRetry(() => import('@/views/Settings')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        key: 'settings.section',
        path: '/settings/:section',
        component: lazyWithRetry(() => import('@/views/Settings')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        key: 'teams',
        path: '/teams',
        component: lazyWithRetry(() => import('@/views/Teams')),
        authority: [],
        meta: {
            pageContainerType: 'contained',
            pageBackgroundType: 'plain',
        },
    },
    {
        // Spec §7 / iter 5 — promote each team from card-opens-modal
        // to a real page. Two columns: Members | Documents. Lives at
        // /teams/{id} so it's bookmarkable and linkable.
        key: 'team-detail',
        path: '/teams/:teamId',
        component: lazyWithRetry(() => import('@/views/TeamDetail')),
        authority: [],
        meta: {
            pageContainerType: 'contained',
            pageBackgroundType: 'plain',
        },
    },
    {
        key: 'trash',
        path: '/trash',
        component: lazyWithRetry(() => import('@/pages/TrashPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            pageBackgroundType: 'plain',
        },
    },
    {
        key: 'snippetDetail',
        path: '/snippets/:category/:id',
        component: lazyWithRetry(() => import('@/pages/SnippetDetailPage')),
        authority: [],
    },
    {
        key: 'snippetCategory',
        path: '/snippets/:category',
        component: lazyWithRetry(() => import('@/pages/SnippetCategoryPage')),
        authority: [],
    },
    {
        key: 'jobs',
        path: '/jobs',
        component: lazyWithRetry(() => import('@/pages/JobsPage')),
        authority: [],
    },
    {
        key: 'convert',
        path: '/convert',
        component: lazyWithRetry(() => import('@/pages/ConvertPage')),
        authority: [],
    },
    {
        key: 'convertType',
        path: '/convert/:type',
        component: lazyWithRetry(() => import('@/pages/ConvertTypePage')),
        authority: [],
    },
    {
        key: 'epub',
        path: '/epub',
        component: lazyWithRetry(() => import('@/views/Epub')),
        authority: [],
    },
    // Reviews dashboard — active sessions + history, filter by format.
    {
        key: 'importReviewList',
        path: '/import-review',
        component: lazyWithRetry(() => import('@/pages/ImportReviewListPage')),
        authority: [],
    },
    // Per-session end-of-review report (History entries link here).
    {
        key: 'importReviewReport',
        path: '/import-review/:sessionId/report',
        component: lazyWithRetry(() => import('@/pages/ImportReviewReportPage')),
        authority: [],
    },
    // Pre-checkout summary (FT-IMP-001). Shown after upload when the
    // user ticked "show summary before importing". Two buttons: Import
    // (calls finalize) or Review (opens the Studio-styled review page).
    {
        key: 'importSummary',
        path: '/import-summary/:sessionId',
        component: lazyWithRetry(() => import('@/pages/ImportSummaryPage')),
        authority: [],
    },
    // Format-specific redesign — route dispatcher sniffs sourceFormat
    // and redirects to /tex/ /docx/ /markdown/ /legacy/ per the plan.
    {
        key: 'importReviewTex',
        path: '/import-review/tex/:sessionId',
        component: lazyWithRetry(() => import('@/pages/TexImportReviewPage')),
        authority: [],
    },
    {
        key: 'importReviewLegacy',
        path: '/import-review/legacy/:sessionId',
        component: lazyWithRetry(() => import('@/pages/ImportReviewPage')),
        authority: [],
    },
    {
        key: 'importReview',
        path: '/import-review/:sessionId',
        component: lazyWithRetry(() => import('@/pages/ImportReviewDispatcher')),
        authority: [],
    },
    // Lilia Convert — the redesigned focused import-review surface (blank layout:
    // drops the app nav for its own rail, like the editor). Reachable directly by
    // URL for now; the dispatcher/list cutover follows once it's validated.
    {
        key: 'convertReview',
        path: '/convert/review/:sessionId',
        component: lazyWithRetry(() => import('@/pages/ConvertReviewPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    // Experimental review-UI variants: three workflow modes and three layout
    // orientations. All share useReviewSession + useDiagnostics.
    {
        key: 'importReviewSpreadsheet',
        path: '/import-review/:sessionId/spreadsheet',
        component: lazyWithRetry(() => import('@/pages/ImportReviewSpreadsheet')),
        authority: [],
    },
    {
        key: 'importReviewKanban',
        path: '/import-review/:sessionId/kanban',
        component: lazyWithRetry(() => import('@/pages/ImportReviewKanban')),
        authority: [],
    },
    {
        key: 'importReviewFocus',
        path: '/import-review/:sessionId/focus',
        component: lazyWithRetry(() => import('@/pages/ImportReviewFocus')),
        authority: [],
    },
    {
        key: 'importReviewSideBySide',
        path: '/import-review/:sessionId/side-by-side',
        component: lazyWithRetry(() => import('@/pages/ImportReviewSideBySide')),
        authority: [],
    },
    {
        key: 'importReviewStacked',
        path: '/import-review/:sessionId/stacked',
        component: lazyWithRetry(() => import('@/pages/ImportReviewStacked')),
        authority: [],
    },
    {
        key: 'importReviewDiagnosticsFirst',
        path: '/import-review/:sessionId/diagnostics-first',
        component: lazyWithRetry(() => import('@/pages/ImportReviewDiagnosticsFirst')),
        authority: [],
    },
    // Isolated Typst preview — dev study surface. Not for end users yet.
    {
        key: 'devTypstPreview',
        path: '/dev/typst-preview/:documentId?',
        component: lazyWithRetry(() => import('@/pages/DevTypstPreviewPage')),
        authority: [],
    },
    // Toolbar isolation harness — every ribbon mounted against a real
    // TipTap editor with a live preview + activity log. Lets us spot
    // which command buttons are wired vs no-op without rendering a
    // full document. Deployed (not DEV-only) so the surface is
    // reachable on mobile devices.
    {
        key: 'devToolbarHarness',
        path: '/dev/toolbars',
        component: lazyWithRetry(() => import('@/pages/dev/ToolbarHarness')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    {
        key: 'latexExport',
        path: '/export/latex/:documentId',
        component: lazyWithRetry(() => import('@/pages/LaTeXExportPage')),
        authority: [],
    },
    {
        // "New from structure" wizard — turns a doc-kind + section spec into
        // a real document of typed blocks (via the API), then opens it in
        // Studio block mode. Deterministic v1 (AI draft stubbed). Blank,
        // gutterless layout: it's a full-screen single-purpose creation surface.
        key: 'newFromStructure',
        path: '/new-from-structure',
        component: lazyWithRetry(() => import('@/pages/NewFromStructurePage/NewFromStructurePage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    {
        // AI document architect — the in-app, paid "Ask AI" conversation
        // screen. Optional :documentId — opened from the editor/studio against
        // a specific doc, or from the nav / ⌘K with no doc (it mints a blank
        // doc on the first accepted diff). Full-screen single-purpose surface.
        key: 'aiArchitect',
        path: '/ai/:documentId?',
        component: lazyWithRetry(() => import('@/pages/AiConversationPage/AiConversationPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    { key: 'legacy.document', path: '/document/:id', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectDocumentToD), authority: [] },
    { key: 'legacy.documentValidate', path: '/document/:id/validate', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectDocumentValidate), authority: [] },
    { key: 'legacy.documentPrint', path: '/document/:id/print', component: named(() => import('@/pages/LegacyRedirects'), (m) => m.RedirectDocumentPrint), authority: [] },
    {
        // Legacy Block URL — redirects to /d/:id
        key: 'studio',
        path: '/studio/:id',
        component: lazyWithRetry(() => import('@/pages/StudioRedirect')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    {
        // ds30 §5/§7 — dedicated full-page validation surface.
        key: 'validation',
        path: '/d/:id/validate',
        component: lazyWithRetry(() => import('@/pages/ValidationPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    {
        // A new document by taps, from the phone's Documents + (Olivia, 10 Oct).
        key: 'mobileNewDocument',
        path: '/new-document',
        component: lazyWithRetry(() => import('@/pages/MobileNewDocumentPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    {
        // Ask Lilia in its own window, or in Keep on top's frame (Olivia, 10 Oct).
        key: 'askWindow',
        path: '/d/:id/ask',
        component: lazyWithRetry(() => import('@/pages/AskWindowPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    {
        key: 'editor',
        path: '/d/:id',
        component: lazyWithRetry(() => import('@/pages/EditorPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    {
        // From-tool handoff — the "Open in Lilia editor" cross-sell
        // destination from lilia-cloud's public tools. Protected: an
        // anonymous visitor bounces to sign-in and returns here (the
        // artifact id rides in the *path* so ProtectedRoute's
        // REDIRECT_URL_KEY preserves it). Creates a real doc from the
        // tool artifact, then redirects into the studio.
        key: 'fromTool',
        path: '/from-tool/:artifactId',
        component: lazyWithRetry(() => import('@/pages/FromToolPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    {
        // Read-only viewer for a stored math-editor diagnostic capture.
        // The DevTools panel's "mine" tab links here; users can also
        // share the URL directly when reporting an issue. Owner-only
        // on the API; admins are deferred.
        key: 'diagnostics.viewer',
        path: '/diagnostics/:refToken',
        component: lazyWithRetry(() => import('@/pages/DiagnosticsViewerPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    {
        key: 'print',
        path: '/d/:id/print',
        component: lazyWithRetry(() => import('@/pages/PrintPage')),
        authority: [],
        meta: {
            pageContainerType: 'gutterless',
            footer: false,
            layout: 'blank',
        },
    },
    // MFA enrollment is only relevant for signed-in users opting into
    // 2FA from their account settings — protected route, not in the
    // public auth flow.
    {
        key: 'mfaEnrollment',
        path: '/mfa-enrollment',
        component: lazyWithRetry(() => import('@/views/auth/MfaEnrollment')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    // Internal: scenarios admin. Owner-gated by AdminGate; non-owners
    // see the locked screen. Blank layout so the full-screen
    // tree+detail layout doesn't get squeezed by app chrome.
    {
        key: 'internal.scenarios',
        path: '/internal/scenarios',
        component: lazyWithRetry(() => import('@/views/internal/ScenariosAdminPage')),
        authority: [],
        meta: { layout: 'blank', pageContainerType: 'gutterless', footer: false },
    },
    // Authed playgrounds — require login (they call real API endpoints).
    // Anonymous visits redirect to sign-in via ProtectedRoute.
    ...authedPlaygroundRoutes,
    ...othersRoute,
]
